import { c as _c } from "react/compiler-runtime";
import React, { ComponentProps, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStandard, ButtonStandardType } from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { Checkbox } from 'record-it-components/input/Checkbox/Checkbox';
import { IconDeprecated, IconType } from 'record-it-components/misc/IconDeprecated/IconDeprecated';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { classes } from 'record-it-components/utils/utils';
import { EntityName } from 'record-it-time-common/EntityName';
import { UserGroupToUser } from '../../../../classes/entities/UserGroupToUser/userGroupToUserTypes';
import { useEntity } from '../../../../hooks/entity/useEntity/useEntity';
import { TimeTrackingConfigurationSelect } from '../../../timeTrackingConfiguration/TimeTrackingConfigurationSelect/TimeTrackingConfigurationSelect';
import styles from './UserGroupToUsersWidgetUser.module.css';
export type UserGroupToUsersWidgetUserProps = PropsWithoutChildren<ComponentProps<'div'>> & {
  userGroupId: string;
  userGroupToUser: UserGroupToUser;
  onDelete: (userGroupToUser: UserGroupToUser) => void;
  onUserGroupUserChanged: (userGroupToUser: UserGroupToUser) => void;
};
export const UserGroupToUsersWidgetUser: FunctionComponent<UserGroupToUsersWidgetUserProps> = React.memo(props => {
  const $ = _c(25);
  const {
    userGroupId,
    userGroupToUser,
    onDelete,
    onUserGroupUserChanged,
    className,
    ...remainingProps
  } = prepareProps({
    props,
    ignoreChildren: true
  });
  const {
    t
  } = useTranslation();
  const user = useEntity(EntityName.User, "getById", userGroupToUser.userId);
  const handleDeleteButtonClick = useEventCallback(() => {
    onDelete(userGroupToUser);
  });
  const handleGroupAdminChanged = useEventCallback(groupAdmin => {
    onUserGroupUserChanged({
      ...userGroupToUser,
      groupAdmin
    });
  });
  const handleTimeTrackingConfigurationIdChanged = useEventCallback(timeTrackingConfigurationId => {
    onUserGroupUserChanged({
      ...userGroupToUser,
      timeTrackingConfigurationId
    });
  });
  const t0 = classes(className, styles.user);
  let t1;
  if ($[0] !== t || $[1] !== user) {
    t1 = user ? user.email : t("components.userGroup.UserGroupToUsersWidget.UserGroupToUsersWidgetUser.unknownUser");
    $[0] = t;
    $[1] = user;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <span data-testid="UserGroupToUsersWidgetUser.UserName">{t1}</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== handleTimeTrackingConfigurationIdChanged || $[6] !== userGroupId || $[7] !== userGroupToUser.timeTrackingConfigurationId) {
    t3 = <TimeTrackingConfigurationSelect userGroupId={userGroupId} value={userGroupToUser.timeTrackingConfigurationId} onValueChanged={handleTimeTrackingConfigurationIdChanged} />;
    $[5] = handleTimeTrackingConfigurationIdChanged;
    $[6] = userGroupId;
    $[7] = userGroupToUser.timeTrackingConfigurationId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t) {
    t4 = t("entities.UserGroup.users.groupAdmin");
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== handleGroupAdminChanged || $[12] !== t4 || $[13] !== userGroupToUser.groupAdmin) {
    t5 = <Checkbox label={t4} value={userGroupToUser.groupAdmin} onValueChanged={handleGroupAdminChanged} data-testid="UserGroupToUsersWidgetUser.GroupAdminCheckbox" />;
    $[11] = handleGroupAdminChanged;
    $[12] = t4;
    $[13] = userGroupToUser.groupAdmin;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <IconDeprecated name="trash" type={IconType.Light} />;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== handleDeleteButtonClick) {
    t7 = <ButtonStandard type={ButtonStandardType.Secondary} onClick={handleDeleteButtonClick} data-testid="UserGroupToUsersWidgetUser.DeleteButton">{t6}</ButtonStandard>;
    $[16] = handleDeleteButtonClick;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== remainingProps || $[19] !== t0 || $[20] !== t2 || $[21] !== t3 || $[22] !== t5 || $[23] !== t7) {
    t8 = <div {...remainingProps} className={t0}>{t2}{t3}{t5}{t7}</div>;
    $[18] = remainingProps;
    $[19] = t0;
    $[20] = t2;
    $[21] = t3;
    $[22] = t5;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
});